import { css, type SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { withOpacity } from '@kaiizen/kai-shiki/core';

// The styles module itself, not the Widgets barrel: this constant is built at import time, and the
// barrel leads back into the Partials that import this file, leaving the badge styles undefined.
import { DEFAULT_TASK_STATUS_BADGE_STYLES, type TaskStatusBadgeStyles } from '@/Client/Widgets/TaskStatusBadge/taskStatusBadgeStyles';
import { ACTIVITY_LOG_TABLE_MARK_SIZE } from '../ActivityLogTable';

/**
 * The task status badge re-skinned to fill the activity log's mark: its icon-only box and icon
 * take the table's mark size instead of the badge's own dense-row 16px / 14px.
 */
export const TASK_ACTIVITY_LOG_BADGE_STYLES: TaskStatusBadgeStyles = {
  ...DEFAULT_TASK_STATUS_BADGE_STYLES,
  iconOnlyContainer: css`
    ${DEFAULT_TASK_STATUS_BADGE_STYLES.iconOnlyContainer};
    label: TASK_ACTIVITY_LOG_BADGE_ICON_ONLY;
    width: ${ACTIVITY_LOG_TABLE_MARK_SIZE}px;
    height: ${ACTIVITY_LOG_TABLE_MARK_SIZE}px;
  `,
  iconOnlyIconSize: ACTIVITY_LOG_TABLE_MARK_SIZE,
};

/** The task activity log's own pieces around the shared table: the edit and note marks, and an edit's tooltip of changes. */
export type TaskActivityLogStyles = {
  editMark: SerializedStyles;
  outlineMark: SerializedStyles;
  changesTable: SerializedStyles;
  changeFieldCell: SerializedStyles;
  changeValueCell: SerializedStyles;
  changeBefore: SerializedStyles;
  detailBefore: SerializedStyles;
  sectionLink: SerializedStyles;
};

const EDIT_MARK_SIZE = 8;

export const DEFAULT_TASK_ACTIVITY_LOG_STYLES: TaskActivityLogStyles = {
  editMark: css`
    label: TASK_ACTIVITY_LOG_EDIT_MARK;
    display: block;
    width: ${EDIT_MARK_SIZE}px;
    height: ${EDIT_MARK_SIZE}px;
    background: ${COLORS.ink};
  `,
  // the line item values tooltip's table, with a before → after value column
  // the edit mark's outline: the task's creation, and reviewers' notes rather than saved changes
  outlineMark: css`
    label: TASK_ACTIVITY_LOG_OUTLINE_MARK;
    display: block;
    box-sizing: border-box;
    width: ${EDIT_MARK_SIZE}px;
    height: ${EDIT_MARK_SIZE}px;
    border: 1px solid ${COLORS.ink};
  `,
  changesTable: css`
    label: TASK_ACTIVITY_LOG_CHANGES_TABLE;
    border-collapse: collapse;

    & td {
      padding: 2px 0;
      vertical-align: top;
    }

    & td + td {
      padding-left: 16px;
    }
  `,
  changeFieldCell: css`
    label: TASK_ACTIVITY_LOG_CHANGE_FIELD_CELL;
    color: ${COLORS.white};
    white-space: nowrap;
  `,
  changeValueCell: css`
    label: TASK_ACTIVITY_LOG_CHANGE_VALUE_CELL;
    text-align: right;
    color: ${COLORS.white};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
  changeBefore: css`
    label: TASK_ACTIVITY_LOG_CHANGE_BEFORE;
    color: ${withOpacity(COLORS.paper, 0.58)};
    font-size: ${pxToRem(10.5)};
    font-weight: 400;
    text-decoration: line-through;
  `,
  // an update's second line: the old value struck through, in the line's own muted tone
  detailBefore: css`
    label: TASK_ACTIVITY_LOG_DETAIL_BEFORE;
    text-decoration: line-through;
  `,
  sectionLink: css`
    label: TASK_ACTIVITY_LOG_SECTION_LINK;
    color: ${COLORS.ink};
    text-decoration: underline;
  `,
};
